import Logo from "@/assets/images/logo/logo-yeda-phone.svg";
import {Input} from "@/components/ui/input.tsx";
import {LoadingButton} from "@/components/ui/button.tsx";
import {Label} from "@/components/ui/label.tsx";
import {Wrapper} from "./components/wrapper.tsx";
import {Link, useLocation} from "react-router";
import {ChangeEvent, FormEvent, ReactNode, useState} from "react";
import {Eye, EyeOff} from "lucide-react";
import {Head} from "@/layout/Head.tsx";
import {useGuestPage} from "@/hooks/useGuestPage.ts";
import {Alert, AlertDescription, AlertTitle} from "@/components/ui/alert.tsx";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogBody, DialogFooter,
} from "@/components/ui/dialog.tsx";
import {useForgotPasswordMutation} from "@/store/auth.ts";

const verificationMessages: Record<string, { variant: 'success' | 'danger' | 'warning' | 'default' | null, title: string, body: string }> = {
    verified: {
        variant: 'success',
        title: 'האימות הושלם בהצלחה!',
        body: 'כתובת האימייל שלך אומתה. כעת ניתן להתחבר לחשבון.',
    },
    token_expire: {
        variant: 'warning',
        title: 'קישור האימות פג תוקף',
        body: 'הקישור ששלחנו אליך פג תוקף. אנא היכנס לדף ההרשמה ובקש שליחת קישור חדש.',
    },
    user_is_verified: {
        variant: 'default',
        title: 'החשבון כבר מאומת',
        body: 'כתובת האימייל שלך כבר מאומתת. ניתן להתחבר ישירות.',
    },
    error: {
        variant: 'danger',
        title: 'שגיאה באימות',
        body: 'אירעה שגיאה בתהליך האימות. אנא נסה שוב או פנה לתמיכה.',
    },
    password_reset: {
        variant: 'success',
        title: 'הסיסמה אופסה בהצלחה!',
        body: 'ניתן כעת להתחבר עם הסיסמה החדשה שלך.',
    },
};

function ForgotPasswordDialog({open, onOpenChange}: {open: boolean; onOpenChange: (v: boolean) => void}) {
    const [email, setEmail] = useState('');
    const [sent, setSent] = useState(false);
    const [forgotPassword, {isLoading}] = useForgotPasswordMutation();

    async function handleSubmit(e: FormEvent) {
        e.preventDefault();
        if (!email.trim()) return;
        await forgotPassword({email});
        setSent(true);
    }

    function handleOpenChange(v: boolean) {
        if (!v) { setEmail(''); setSent(false); }
        onOpenChange(v);
    }

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent className="sm:max-w-md" dir="rtl">
                <DialogHeader>
                    <DialogTitle>שכחתי סיסמה</DialogTitle>
                    <DialogDescription>
                        הכנס את כתובת המייל שלך ונשלח אליך קישור לאיפוס הסיסמה.
                    </DialogDescription>
                </DialogHeader>
                <DialogBody>
                {sent ? (
                    <Alert variant="success">
                        <AlertTitle>הבקשה התקבלה</AlertTitle>
                        <AlertDescription>
                            אם קיים חשבון עם כתובת המייל שציינת, ישלח אליך קישור לאיפוס הסיסמה תוך מספר דקות.
                            בדוק גם את תיקיית ה-ספאם.
                        </AlertDescription>
                    </Alert>
                ) : (
                    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
                        <div>
                            <Label htmlFor="forgot-email">כתובת אימייל</Label>
                            <Input
                                id="forgot-email"
                                type="email"
                                value={email}
                                onChange={e => setEmail(e.target.value)}
                                placeholder="your@email.com"
                                className="mt-2"
                                required
                            />
                        </div>
                        <DialogFooter className="flex-row-reverse gap-2 sm:justify-start">
                            <LoadingButton isLoading={isLoading} type="submit">
                                שלח קישור לאיפוס
                            </LoadingButton>
                        </DialogFooter>
                    </form>
                )}
                </DialogBody>
            </DialogContent>
        </Dialog>
    );
}

export default function LoginPage() {

    const [{username, password}, setData] = useState({username: '', password: ''});
    const [error, setError] = useState<string>('');
    const [showPassword, setShowPassword] = useState(false);
    const [forgotOpen, setForgotOpen] = useState(false);
    const {login, isLoading} = useGuestPage();
    const location = useLocation();

    const verificationParam = Object.keys(verificationMessages).find(
        key => new URLSearchParams(location.search).get(key) === 'true'
    );
    const verificationAlert = verificationParam ? verificationMessages[verificationParam] : null;

    function handleChange(e: ChangeEvent<HTMLInputElement>) {
        const {name, value} = e.target;
        setData(prev => ({...prev, [name]: value}));
    }

    async function handleSubmit(e: FormEvent<HTMLFormElement>) {
        e.preventDefault();
        if ([username, password].some(t => t.trim().length === 0)) {
            return setError('אנא מלא את כל השדות');
        }
        await login({username, password}).catch(err => {
            setError(err.response.data.msg);
        });
    }

    return (
        <Wrapper>
            <Head title="התחברות"/>
            <ForgotPasswordDialog open={forgotOpen} onOpenChange={setForgotOpen}/>

            {/* ── Form panel ── */}
            <div className="flex flex-col justify-center items-center w-full lg:w-1/2 overflow-y-auto px-6 py-10 sm:px-12 lg:px-14">
                <div className="w-full max-w-sm flex flex-col gap-7">

                    <div>
                        <img alt="ידע פון" src={Logo} className="h-10 w-auto"/>
                    </div>

                    <div className="text-2xl font-semibold text-gray-900">התחברות לחשבון</div>

                    <Alert variant="danger" className="bg-red-50 border-red-200">
                        <AlertTitle className="text-base">שים לב!</AlertTitle>
                        <AlertDescription className="!text-red-700 text-sm">
                            גם לבעלי חשבון בממשק הישן חובה לבצע רישום מחדש על מנת להשתמש בממשק החדש.{' '}
                            <Link className="border-b hover:opacity-80 border-dotted transition" to="/register">הירשם כאן</Link>
                        </AlertDescription>
                    </Alert>

                    {verificationAlert && (
                        <Alert variant={verificationAlert.variant}>
                            <AlertTitle className="text-base">{verificationAlert.title}</AlertTitle>
                            <AlertDescription className="text-sm">{verificationAlert.body}</AlertDescription>
                        </Alert>
                    )}

                    <form onSubmit={handleSubmit} className="flex flex-col gap-5">
                        <div>
                            <Label htmlFor="username">כתובת אימייל</Label>
                            <Input name="username" id="username" type="text" value={username} onChange={handleChange}
                                className="w-full h-10 px-3 mt-2 border border-gray-300 rounded-lg focus:outline-none focus:border-sky-500"/>
                        </div>
                        <div>
                            <Label htmlFor="password">סיסמא</Label>
                            <div className="relative mt-2">
                                <Input name="password" id="password" type={showPassword ? 'text' : 'password'} value={password} onChange={handleChange}
                                    className="w-full h-10 px-3 pe-10 border border-gray-300 rounded-lg focus:outline-none focus:border-sky-500"/>
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(v => !v)}
                                    className="absolute inset-y-0 end-0 flex items-center px-3 text-gray-400 hover:text-gray-600 transition"
                                    tabIndex={-1}
                                    aria-label={showPassword ? 'הסתר סיסמה' : 'הצג סיסמה'}
                                >
                                    {showPassword ? <EyeOff size={16}/> : <Eye size={16}/>}
                                </button>
                            </div>
                        </div>
                        <div className="flex justify-end -mt-1">
                            <button type="button" onClick={() => setForgotOpen(true)}
                                className="text-sm text-sky-600 hover:text-sky-800 transition cursor-pointer">
                                שכחתי סיסמה
                            </button>
                        </div>
                        <LoadingButton isLoading={isLoading} size="lg" className="w-full">כניסה</LoadingButton>
                        {error && <div className="text-red-500 text-sm">{error}</div>}
                    </form>

                    <div className="flex gap-1.5 items-center">
                        <span className="text-sm text-gray-500">חדש כאן?</span>
                        <Link className="text-sm text-gray-400 hover:text-gray-600 border-b border-dotted transition" to="/register">הירשם</Link>
                    </div>
                </div>
            </div>

            {/* ── Decorative panel – desktop only ── */}
            <LoginDecorativePanel/>
        </Wrapper>
    );
}

/* Decorative right-side panel (lg+ only) */
function LoginDecorativePanel() {
    return (
        <div
            className="hidden lg:block lg:w-1/2 shrink-0 relative overflow-hidden"
            style={{
                background: [
                    'radial-gradient(ellipse at 44% 22%, rgba(130, 180, 255, 0.38) 0%, transparent 54%)',
                    'radial-gradient(ellipse at 72% 76%, rgba(200, 90, 30, 0.48) 0%, transparent 48%)',
                    'linear-gradient(158deg, #19293f 0%, #1b3656 22%, #1d3e63 50%, #5c2810 78%, #351208 100%)',
                ].join(', '),
            }}
        >
            {/* Headline */}
            <div className="absolute top-14 inset-x-0 px-10 text-right z-10 pointer-events-none">
                <p className="text-white text-4xl xl:text-5xl font-bold leading-tight drop-shadow-lg">
                    הטלפון שלך
                    <br/>
                    הטכנולוגיה שלנו
                </p>
            </div>

            {/* Feature cards */}
            <div className="absolute bottom-16 right-10 flex flex-col gap-3 w-64 z-10">
                <FeatureCard
                    title="קו יציב ואמין"
                    description="שירות יציב, ואמין. כמות עצומה של שרתים ללא תקלות וקריסות"
                    iconBg="bg-rose-100"
                    icon={
                        <svg className="w-4 h-4 text-rose-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/>
                        </svg>
                    }
                />
                <FeatureCard
                    title="פונקציונליות והתאמה"
                    description="בזכות הטכנולוגיה המתקדמת שפיתחנו, אנו יכולים להתאים אישית כל מה שתצטרך"
                    iconBg="bg-blue-100"
                    icon={
                        <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4"/>
                        </svg>
                    }
                />
            </div>

            {/* Nav sidebar */}
            <div className="absolute left-8 top-1/2 -translate-y-1/2 flex flex-col items-center gap-3 z-10">
                <div className="bg-[#1c2233]/90 backdrop-blur-sm rounded-2xl py-3 px-2.5 flex flex-col gap-2.5 shadow-2xl">
                    {/* Active icon */}
                    <div className="bg-blue-600 rounded-xl p-2.5">
                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/>
                        </svg>
                    </div>
                    {([
                        'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z',
                        'M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z',
                        'M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z',
                        'M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z',
                        'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z',
                    ] as string[]).map((d, i) => (
                        <div key={i} className="p-2.5 rounded-lg text-gray-400 hover:text-white hover:bg-white/10 transition-colors cursor-default">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={d}/>
                            </svg>
                        </div>
                    ))}
                </div>
                <div className="bg-[#1c2233]/90 backdrop-blur-sm rounded-2xl p-2.5 shadow-2xl">
                    <img alt="ידע פון" src={Logo} className="w-8 h-8"
                        style={{filter: 'brightness(0) invert(1) opacity(0.85)'}}/>
                </div>
            </div>
        </div>
    );
}

function FeatureCard({title, description, icon, iconBg}: {
    title: string;
    description: string;
    icon: ReactNode;
    iconBg: string;
}) {
    return (
        <div className="bg-white/95 backdrop-blur-sm rounded-2xl p-4 shadow-lg">
            <div className="flex items-start gap-3">
                <div className="text-right flex-1">
                    <div className="font-bold text-gray-900 text-sm mb-1">{title}</div>
                    <div className="text-gray-500 text-xs leading-relaxed">{description}</div>
                </div>
                <div className={`${iconBg} rounded-full p-2 shrink-0`}>{icon}</div>
            </div>
        </div>
    );
}
